import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  ErrorState,
  Pagination,
  Select,
  Table,
} from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import { RefreshButton } from '@/shared/RefreshButton';
import { useUrlFilters } from '@/shared/hooks/use-url-filters';
import { useEffectiveRoles } from '@/shared/view-as/use-effective-roles';
import { hasAnyRole } from '@/shared/guards/roles';
import type {
  PendingAlertItem,
  PendingSourceActions,
} from '../../domain/entities/pending-alert.entity';
import {
  usePendingSourceFacets,
  usePendingSourceItems,
  type PendingSourceCard,
} from '../hooks/use-pending-alerts';
import { ClosePendingAlertModal } from '../components/ClosePendingAlertModal';
import { PendingAlertHistoryButton } from '../components/PendingAlertHistoryModal';
import {
  formatDateTime,
  Mono,
  PendingAlertStatusBadge,
} from '../shared/display';

const PER_PAGE = 20;

/**
 * Um card aberto por vez, e ele, a página e o tipo vivem na URL: um link
 * reabre a mesma coisa. Tudo `string` porque é o que o `useUrlFilters` serve.
 */
const EMPTY_FILTERS = { source: '', type: '', page: '' };

function parsePage(raw: string): number {
  const page = Number(raw);
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

/**
 * Os casos que a linha carrega dentro de si (`detailRows`), num painel que só
 * abre quando alguém clica.
 *
 * Existe porque nem toda fila humana é ponteiro para outra coleção: o alerta de
 * faturas paradas do Sales **é** a lista, e sem isto a linha diria que há um
 * problema sem dizer em quais faturas. Fonte que não preenche `detailRows` não
 * renderiza nada aqui — as demais linhas seguem idênticas ao que eram.
 *
 * `<details>` nativo, e não estado de linha: a Table do design-system não tem
 * linha expansível, e acrescentar uma para um único caso mudaria um componente
 * compartilhado por todas as telas do app.
 */
function PendingAlertDetailsPanel({ item }: { item: PendingAlertItem }) {
  const rows = item.detailRows;
  if (!rows || rows.length === 0) return null;
  return (
    <details className="mt-1">
      <summary className="text-(--color-accent) cursor-pointer text-xs hover:underline">
        {item.detailsSummary ??
          `Ver ${rows.length} ${rows.length === 1 ? 'caso' : 'casos'}`}
      </summary>
      {item.detailsNote && (
        <p className="text-(--color-text-secondary) mt-1 text-xs">
          {item.detailsNote}
        </p>
      )}
      <ul className="border-(--color-border) mt-1 max-h-72 space-y-1 overflow-auto border-l pl-3">
        {rows.map((detail) => (
          <li key={`${detail.label}:${detail.value}`} className="text-xs">
            <span className="text-(--color-text-secondary)">
              {detail.label}
            </span>{' '}
            <Mono>
              {detail.href ? (
                <Link
                  to={detail.href}
                  className="text-(--color-accent) hover:underline"
                >
                  {detail.value}
                </Link>
              ) : (
                detail.value
              )}
            </Mono>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** A badge do card fechado: o total, ou o motivo de não haver um. */
function TotalBadge({ card }: { card: PendingSourceCard }) {
  if (card.isLoading) return <Badge>…</Badge>;
  if (card.isError) return <Badge variant="error">não carregou</Badge>;
  return (
    <Badge variant={card.total > 0 ? 'warning' : 'success'}>{card.total}</Badge>
  );
}

interface SourceListProps {
  card: PendingSourceCard;
  type: string;
  page: number;
  onTypeChange: (type: string) => void;
  onPageChange: (page: number) => void;
  onClose: (item: PendingAlertItem, actions: PendingSourceActions) => void;
}

/** O corpo do card aberto — a listagem paginada de uma fonte só. */
function SourceList({
  card,
  type,
  page,
  onTypeChange,
  onPageChange,
  onClose,
}: SourceListProps) {
  const { source, facets, total } = card;
  const roles = useEffectiveRoles();
  const { data, isLoading, isFetching, isError, refetch } =
    usePendingSourceItems(source, { page, perPage: PER_PAGE, type });

  // Fechar o último item da última página deixaria a tela em "3 de 2",
  // vazia, com a fila cheia: volta para a última página que existe.
  const lastPage = Math.max(1, Math.ceil((data?.total ?? 0) / PER_PAGE));
  useEffect(() => {
    if (data && page > lastPage) onPageChange(lastPage);
  }, [data, page, lastPage, onPageChange]);

  const actions = source.actions;
  const canClose = actions ? hasAnyRole(roles, actions.writeRoles) : false;

  const typeOptions = [
    // Faceta que falhou não tem contagem: "(0)" ao lado de uma fila cheia
    // contradiria o erro da badge.
    {
      value: '',
      label: card.isError ? 'Todos os tipos' : `Todos os tipos (${total})`,
    },
    ...facets.map((facet) => ({
      value: facet.value,
      label: `${facet.label} (${facet.count})`,
    })),
  ];

  const columns: ColumnDef<PendingAlertItem>[] = [
    {
      key: 'type',
      header: 'Tipo',
      render: (row) => (
        <span className="text-(--color-text-primary) text-sm">
          {row.typeLabel}
        </span>
      ),
    },
    {
      key: 'entity',
      header: 'Entidade',
      render: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-(--color-text-secondary) text-xs">
            {row.entityTypeLabel}
          </span>
          {/* Sem `entityHref` a entidade não tem tela de destino nesta fonte —
              o id fica como texto em vez de virar link para lugar nenhum. */}
          <Mono>
            {row.entityHref ? (
              <Link
                to={row.entityHref}
                className="text-(--color-accent) hover:underline"
              >
                {row.entityId}
              </Link>
            ) : (
              row.entityId
            )}
          </Mono>
          <PendingAlertDetailsPanel item={row} />
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex flex-col items-start gap-1">
          <PendingAlertStatusBadge
            status={row.status}
            label={row.statusLabel}
          />
          <span className="text-(--color-text-secondary) text-xs">
            desde {formatDateTime(row.inThisStatusSince)}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (row) => {
        if (!actions) return null;
        return (
          <div className="flex flex-wrap gap-2">
            <PendingAlertHistoryButton
              itemId={row.id}
              fetchHistory={actions.fetchHistory}
            />
            {canClose && (
              <Button variant="secondary" onClick={() => onClose(row, actions)}>
                Fechar
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <Select
        id={`pending-alerts-type-${source.key}`}
        aria-label={`Filtrar ${source.label} por tipo`}
        options={typeOptions}
        value={type}
        onChange={(e) => onTypeChange(e.target.value)}
      />

      {isError ? (
        <ErrorState
          message={`Não foi possível carregar as pendências de ${source.label}. Isto não quer dizer que não há nenhuma.`}
          onRetry={() => void refetch()}
        />
      ) : (
        <>
          <Table
            columns={columns}
            data={data?.items ?? []}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage={
              type
                ? 'Nenhuma pendência deste tipo. Escolha "Todos os tipos" para ver o resto.'
                : 'Nenhuma pendência aberta. Nada aguardando decisão agora.'
            }
            striped
          />
          <Pagination
            page={page}
            total={data?.total ?? 0}
            perPage={PER_PAGE}
            loading={isFetching}
            label="pendência"
            onPageChange={onPageChange}
          />
        </>
      )}
    </div>
  );
}

export function PendingAlertsPage() {
  const [filters, setFilters] = useUrlFilters(EMPTY_FILTERS);
  const { cards, isFetching, refetch } = usePendingSourceFacets();
  const [closing, setClosing] = useState<{
    item: PendingAlertItem;
    actions: PendingSourceActions;
  } | null>(null);

  const toggle = (key: string) =>
    setFilters({
      ...EMPTY_FILTERS,
      source: filters.source === key ? '' : key,
    });

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Central de Pendências
          </h1>
          <p className="text-(--color-text-secondary) mt-1 max-w-3xl text-sm">
            Alertas operacionais abertos dos módulos que você acompanha, um card
            por fila. Cada linha é algo que ainda espera uma decisão humana — a
            mais antiga primeiro.
          </p>
        </div>
        <RefreshButton onRefresh={refetch} isRefreshing={isFetching} />
      </div>

      <div className="space-y-3">
        {cards.map((card) => {
          const { source } = card;
          const open = filters.source === source.key;
          // Tipo que saiu da faceta (todos fechados) não fica filtrando por
          // trás de um select que mostra "Todos os tipos".
          const type =
            open &&
            (card.isLoading ||
              card.facets.some((facet) => facet.value === filters.type))
              ? filters.type
              : '';
          const panelId = `pending-source-${source.key}`;
          return (
            <section
              key={source.key}
              className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border"
            >
              <button
                type="button"
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                onClick={() => toggle(source.key)}
                className="hover:bg-(--color-bg-secondary) flex w-full items-center justify-between gap-3 rounded-xl p-4 text-left transition-colors"
              >
                <span className="text-(--color-text-primary) font-semibold">
                  {source.label}
                </span>
                <span className="flex items-center gap-3">
                  <TotalBadge card={card} />
                  <span
                    aria-hidden
                    className={`text-(--color-text-secondary) transition-transform ${open ? 'rotate-90' : ''}`}
                  >
                    ›
                  </span>
                </span>
              </button>
              {open && (
                <div
                  id={panelId}
                  className="border-(--color-border) border-t p-2 sm:p-4"
                >
                  <SourceList
                    card={card}
                    type={type}
                    page={parsePage(filters.page)}
                    onTypeChange={(next) =>
                      setFilters({ ...filters, type: next, page: '' })
                    }
                    onPageChange={(next) =>
                      setFilters({ ...filters, page: String(next) })
                    }
                    onClose={(item, actions) => setClosing({ item, actions })}
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>

      {closing && (
        <ClosePendingAlertModal
          item={closing.item}
          actions={closing.actions}
          open
          onClose={() => setClosing(null)}
        />
      )}
    </div>
  );
}
